import { debounce } from 'lodash-es'
import {
    defaultQueryState,
    defaultResultsState,
    SEARCH_HISTORY,
    SEARCHSTATE,
} from '.'
import type { ListSearchOptions, SearchOptions } from '../../types'
import {
    SearchResultEntry,
    SearchResults,
    TabbedResults,
} from '../../types/state'
import { SearchEvent } from '../constants/enums'
import type { EntryResult } from '../libraries/smart-search/types'
import { locales } from '../locales/en'
import { getSessionKey } from '../mfcLogic/history'
import { localStorageAvailable, setLocalStorage } from '../utilities/storage'
import { pubSubSearch } from './pubSub'

const MAX_SEARCH_QUERIES_STORED = 5
const HISTORY_ADD_ITEM_DELAY = 500

export const isQueryDirty = (
    queryA: SearchOptions = {},
    queryB: SearchOptions = {},
) => queryA.search !== queryB.search || queryA.type !== queryB.type

const hasRefreshTimeElapsed = (lastSearched: number, currentTime: number) => {
    return currentTime - lastSearched > 10 * 1000
}

/** send `SEARCHSTATE.query` to the worker */
export function search() {
    const currentTime = Date.now()
    const queryDirty = isQueryDirty(
        SEARCHSTATE.results.query,
        SEARCHSTATE.query,
    )

    if (
        queryDirty ||
        hasRefreshTimeElapsed(SEARCHSTATE.results.timestamp, currentTime)
    ) {
        setSearchLoading(true)
        updateSearchResults({
            query: SEARCHSTATE.query,
            timestamp: currentTime,
        })
        pubSubSearch.emit(SearchEvent.QUERY, SEARCHSTATE.query, true)
    }
}

/** send the list query to the worker to compute */
export function searchList(searchPayload: ListSearchOptions) {
    pubSubSearch.emit(SearchEvent.QUERY_LIST, searchPayload, true)
}

export function setSearchLoading(loading: boolean) {
    SEARCHSTATE.loading = loading
    pubSubSearch.emit(SearchEvent.LOADING, SEARCHSTATE.loading, true)
}

export function setSearchQuery(query: SearchOptions) {
    SEARCHSTATE.query = query
    pubSubSearch.emit(SearchEvent.UPDATE_QUERY, SEARCHSTATE.query, true)
}

export const normalizeSearchQuery = (string) => {
    if (!string || !string.trim()) {
        return null
    }

    return string.trim().toLocaleLowerCase()
}

export function updateSearchView(query: Partial<SearchOptions>) {
    if (SEARCHSTATE.query && SEARCHSTATE.query.view === query.view) {
        console.log(
            '[updateSearchView] Same view:',
            SEARCHSTATE.query.view,
            query.view,
        )
        return
    }

    const nextQuery = { ...SEARCHSTATE.query, ...query }

    setSearchQuery(nextQuery)
}

export function updateSearchQuery(query: Partial<SearchOptions>) {
    const resolvedQuery = { ...query }

    const isSameQuerySearch =
        normalizeSearchQuery(SEARCHSTATE.query?.search) ===
        normalizeSearchQuery(query.search)

    const isSameQueryType =
        normalizeSearchQuery(SEARCHSTATE.query?.type) ===
        normalizeSearchQuery(query.type)

    if (isSameQuerySearch && isSameQueryType) {
        console.log(
            '[updateSearchQuery] Same query:',
            SEARCHSTATE.query?.search,
            query.search,
            SEARCHSTATE.query?.type,
            query.type,
        )
        return
    }

    if (isSameQuerySearch) {
        resolvedQuery.search = SEARCHSTATE.query?.search
    }

    if (isSameQueryType) {
        resolvedQuery.type = SEARCHSTATE.query?.type
    }

    const nextQuery = { ...SEARCHSTATE.query, ...resolvedQuery }

    setSearchQuery(nextQuery)
}

export function deleteSearchQueryExternal() {
    delete SEARCHSTATE.query?.external
}

export function resetSearchQuery() {
    setSearchQuery({ ...defaultQueryState })
    setSearchLoading(false)
}

export function setSearchResults(results: SearchResults, force?) {
    if (SEARCHSTATE.results.timestamp > results.timestamp && !force) {
        console.log('[setSearchResults] Disregarding older results.')
        return
    }

    SEARCHSTATE.results = results
    pubSubSearch.emit(SearchEvent.RESULTS, SEARCHSTATE.results, true)
}

export function updateSearchResults(results: Partial<SearchResults>) {
    const nextResults = { ...SEARCHSTATE.results, ...results }
    setSearchResults(nextResults)
}

export function resetSearchResults() {
    setSearchResults({ ...defaultResultsState }, true)
    setSearchLoading(false)
}

export function resetSearch(view?) {
    SEARCHSTATE.results = { ...defaultResultsState }
    SEARCHSTATE.loading = false
    setSearchQuery({ ...defaultQueryState, view })
}

function setSearchHistory(history: Array<string> = []) {
    SEARCHSTATE.history = history
    pubSubSearch.emit(SearchEvent.UPDATE_HISTORY, SEARCHSTATE.history, true)
    if (localStorageAvailable) {
        const SEARCH_RESULT_HISTORY = getSessionKey(SEARCH_HISTORY)
        setLocalStorage(SEARCH_RESULT_HISTORY, JSON.stringify(history))
    }
}

export const addSearchHistoryItem = debounce((item: string) => {
    const { history = [] } = SEARCHSTATE
    if (history[0] === item || !item) {
        return history
    }

    const indexOfItem = history.indexOf(item)
    if (indexOfItem !== -1) {
        const newHistory = [...history]
        const existingItem = newHistory.splice(indexOfItem, 1)[0]
        newHistory.unshift(existingItem)
        setSearchHistory(newHistory)
        return
    }

    const _history = [...history]
    _history.unshift(item)
    const newHistory = _history.splice(0, MAX_SEARCH_QUERIES_STORED)
    setSearchHistory(newHistory)
}, HISTORY_ADD_ITEM_DELAY)

export function deleteSearchHistoryItem(
    index: number,
    callback?: (deletedItem: string, history?: Array<string>) => void,
) {
    const { history } = SEARCHSTATE
    if (!Array.isArray(history) || history.length === 0) return

    const newHistory = [...history]
    const deletedItem = newHistory.splice(index, 1)

    setSearchHistory(newHistory)
    callback?.(deletedItem[0], newHistory)
}

export function resetSearchHistory(
    callback?: (history?: Array<string>) => void,
) {
    setSearchHistory([])
    callback?.([])
}

export interface SearchResultUser {
    entry: SearchResultEntry
    info: EntryResult[]
    score?: number
}

export function buildTabbedResults(
    results: Array<SearchResultUser>,
    searchString = '',
): TabbedResults {
    const collection = buildCollection(results)
    const { names, topics, tags } = collection

    const tabbedResults = {
        ...(names.length > 0 && {
            name: {
                data: names.map(({ entry }) => entry),
                id: 'name' as const,
                title: locales.search.names,
                searchString,
            },
        }),
        ...(topics.length > 0 && {
            topic: {
                data: topics.map(({ entry }) => entry),
                id: 'topic' as const,
                title: locales.search.topics,
                searchString,
            },
        }),
        ...(tags.length > 0 && {
            tags: {
                data: tags.map(({ entry }) => entry),
                id: 'tags' as const,
                title: locales.search.tags,
                searchString,
            },
        }),
    }

    return tabbedResults
}

interface NamesResult extends SearchResultUser {
    names: EntryResult
}
interface TopicsResult extends SearchResultUser {
    topics: EntryResult
}
interface TagsResult extends SearchResultUser {
    tags: EntryResult & { start: number; end: number }
}
interface Collection {
    names: Array<NamesResult>
    topics: Array<TopicsResult>
    tags: Array<TagsResult>
}

function buildCollection(results: Array<SearchResultUser>) {
    const collection = results.reduce<Collection>(
        (collection, result) => {
            const { info } = result
            const names = info.find(({ field }) => field === 'name')
            if (names) {
                collection.names.push({ ...result, names })
            }
            const topics = info.find(({ field }) => field === 'topic')
            if (topics) {
                collection.topics.push({ ...result, topics })
            }
            const tags = info.find(({ field }) => field === 'tags')
            if (tags) {
                const { patterns } = tags
                const { matchIndexes: firstMatchIndexes } = patterns[0]
                const start = firstMatchIndexes[0]
                const { matchIndexes: lastMatchIndexes } =
                    patterns[patterns.length - 1]
                const end = lastMatchIndexes[lastMatchIndexes.length - 1]
                collection.tags.push({
                    ...result,
                    tags: { ...tags, start, end },
                })
            }
            return collection
        },
        { names: [], topics: [], tags: [] },
    )
    return collection
}

export function clearHomePageSearchResults() {
    if (!SEARCHSTATE?.query?.fromHomePage) return
    resetSearchQuery()
    resetSearchResults()
}
